import type { Route } from './+types/recurring';
import { Link } from 'react-router';
import { Breadcrumbs, breadcrumbSchemaItems } from '~/components/breadcrumbs';
import { RelatedPages } from '~/components/related-pages';
import { buildMeta, breadcrumbSchema, schemaFor } from '~/lib/seo';
import { SITE, PRICING } from '~/data/site';

export const meta: Route.MetaFunction = () => {
  const pageSchema = schemaFor('/recurring');
  const schemas: Record<string, unknown>[] = Array.isArray(pageSchema)
    ? pageSchema
    : pageSchema
      ? [pageSchema]
      : [];
  return buildMeta({
    title: 'Recurring Cleaning Service | Tampa Bay',
    description:
      `Recurring residential cleaning across Tampa Bay — weekly, biweekly, or every 4 weeks. Same crew every visit. Per-square-foot pricing from ${PRICING.weeklyRate.toFixed(2)}–${PRICING.monthly4Rate.toFixed(2)}/sqft. ${PRICING.minimum} minimum.`,
    path: '/recurring',
    jsonLd: [
      ...schemas,
      breadcrumbSchema(breadcrumbSchemaItems('/recurring')),
    ],
  });
};

export default function RecurringPage() {
  const exampleSqft = 2000;
  const tiers = [
    {
      cadence: 'Weekly',
      rate: PRICING.weeklyRate,
      slug: '/weekly',
      who: 'Busy households, families with kids, anyone who never wants to think about cleaning again.',
      blurb: 'The lowest per-visit rate. Same crew every week — they learn your home and your standards.',
    },
    {
      cadence: 'Biweekly',
      rate: PRICING.biweeklyRate,
      slug: '/biweekly',
      who: 'Our most popular schedule. Right for most homes.',
      blurb: 'Enough cadence to stay ahead of buildup, light enough on the calendar to feel manageable.',
    },
    {
      cadence: 'Every 4 weeks',
      rate: PRICING.monthly4Rate,
      slug: '/monthly',
      who: 'Smaller homes, low-traffic households, or homes that have steady upkeep between visits.',
      blurb: 'A monthly reset — deeper than maintenance, lighter than a deep clean.',
    },
  ];

  return (
    <main id="main-content">
      <Breadcrumbs path="/recurring" pageLabel="Recurring Cleaning" />
      {/* HEADER */}
      <section className="section">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Recurring Cleaning</p>
          <h1 className="display-lg page-h1-full">
            One clean, then another, then another &mdash;{' '}
            <em className="italic-display">that's how homes stay right.</em>
          </h1>
          <p className="lede chapter-lede">
            Recurring cleaning is the difference between a house that needs a deep clean every six
            months and one that always feels handled. Three cadences, one team, one standard.
          </p>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">Three recurring schedules.</h2>
          <p className="chapter-lede mt-2">
            All three include the same scope. The cadence determines the rate.
          </p>

          <div className="tier-list">
            {tiers.map((t) => {
              const examplePrice = Math.max(Math.round(exampleSqft * t.rate), PRICING.minimum);
              return (
                <Link
                  key={t.slug}
                  to={t.slug}
                  className="recurring-tier-card"
                >
                  <div className="tier-head">
                    <h3 className="display-sm tier-name">{t.cadence}</h3>
                    <div className="tier-rate">
                      <div className="tier-rate-value">
                        ${t.rate.toFixed(2)}/sqft
                      </div>
                      <div className="tier-rate-note">
                        2,000 sqft = ${examplePrice}
                      </div>
                    </div>
                  </div>
                  <p className="tier-kicker">
                    Right for: {t.who}
                  </p>
                  <p className="tier-desc">
                    {t.blurb}
                  </p>
                  <p className="tier-link">
                    Learn more about {t.cadence.toLowerCase()} cleaning →
                  </p>
                </Link>
              );
            })}
          </div>

          <p className="marginalia mt-4">
            ${PRICING.minimum} job minimum applies to every visit. Larger homes pay a lower rate per square foot.
          </p>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="section-sm">
        <div className="container-narrow">
          <h2 className="display-md">What's included on every recurring visit.</h2>
          <p className="chapter-lede mt-2">
            Each visit is a full clean, not a touch-up. The recurring rate reflects the cadence, not the scope.
          </p>
          <ul className="plain-list mt-6">
            <li>All bathrooms — toilets, sinks, mirrors, tubs/showers, floors</li>
            <li>Kitchen — counters, exterior of appliances, sink, stovetop, floors</li>
            <li>All living areas — dust, vacuum, mop hard floors</li>
            <li>Bedrooms — dust, vacuum, change linens (when laid out)</li>
            <li>Trash + recycling out, bins relined</li>
            <li>Same crew, every visit — they learn your home</li>
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="section-sm bg-forest">
        <div className="container-narrow text-center">
          <h2 className="display-md text-bone">Get your exact price.</h2>
          <p className="chapter-lede text-bone chapter-lede-dark">
            Enter your home size and pick a cadence. See your monthly cost in under a minute.
          </p>
          <Link to="/get-a-quote" className="btn btn-brass">Calculate my price →</Link>
        </div>
      </section>
    </main>
  );
}
